<template>
  <div>
    <main-header>
      <v-spacer />
      <v-btn
        v-for="item in buttons"
        :key="item.title + '-menu'"
        :loading="item.loading || false"
        color="primary"
        class="ma-2 hidden-sm-and-down"
        dark
        @click="item.click"
      >
        {{ item.title }}
        <v-icon right>
          {{ item.icon }}
        </v-icon>
      </v-btn>
      <v-menu class="hidden-md-and-up">
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            color="primary"
            class="hidden-md-and-up"
            v-on="on"
          >
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item dense>
            <v-btn
              color="primary"
              class="ma-2"
              width="100%"
              dark
              @click="showUploadDialog"
            >
              Upload
              <v-icon right>
                mdi-upload
              </v-icon>
            </v-btn>
          </v-list-item>
        </v-list>
      </v-menu>
    </main-header>

    <v-container fluid>
      <v-data-table
        :headers="payinFeesTableHeaders"
        :items="fees"
        class="elevation-1 table"
        multi-sort
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :loading="loadingTable"
        hide-default-footer
        item-key="id"
        :items-per-page="perPage"
        fixed-header
        @update:sort-by="getFeesWithSort"
        @update:sort-desc="getFeesWithSort"
      >
        <template v-slot:top>
          <v-toolbar
            class="toolbar"
            height="84"
            flat
          >
            <div class="toolbar-content-special d-flex align-center w-100">
              <SelectProject
                v-if="tenantsList && tenantsList.length > 0"
                :tenants-list="tenantsList"
                @update="onChangedSelectedProject"
              />
              <v-spacer />
              <FilterInput
                v-model="filter"
                :items="payinFeesTableFilters"
                filter-range-from-payload-string=">"
                filter-range-to-payload-string="<"
              />
            </div>
          </v-toolbar>

          <v-divider />
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex">
            <v-icon
              small
              class="mr-2"
              :disabled="!isAdmin && !isEditor"
              @click="() => editItem(item)"
            >
              mdi-pencil
            </v-icon>
            <v-icon
              small
              class="mr-2"
              :disabled="!isAdmin && !isEditor"
              @click="() => deleteItem(item)"
            >
              mdi-trash-can
            </v-icon>
          </div>
        </template>

        <template v-slot:no-data>
          <v-btn
            color="primary"
            dark
            @click="getFees"
          >
            Refresh
          </v-btn>
        </template>

        <template v-slot:footer>
          <v-container
            fluid
            class="mt-4"
          >
            <v-row>
              <v-col
                cols="9"
                align-self="center"
              >
                <v-pagination
                  v-model="page"
                  class="b-pagination"
                  :length="totalPages"
                  :total-visible="11"
                  @input="onChangePage"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="perPage"
                  class="select-per-page"
                  :items="perPageItems"
                  placeholder="Items per page"
                  @change="perPage = parseInt($event, 10)"
                />
              </v-col>
            </v-row>
          </v-container>
          <div class="text-center py-2" />
        </template>
      </v-data-table>
    </v-container>

    <UploadPayinFeeModal
      v-model="uploadDialog"
      @upload-success="getFees"
    />

    <CreatePayinFeeModal
      v-if="createDialog"
      v-model="createDialog"
      :edited-item="editedItem"
      @on-create="getFees"
    />

    <DeletePayinFeeModal
      v-if="deleteModal"
      v-model="deleteModal"
      :fee-item="deletedItem"
      @on-delete="getFees"
    />
  </div>
</template>

<script>
import MainHeader from "../components/MainHeader";
import { mapGetters } from "vuex";
import {
  payinFeesTableHeaders,
  payinFeesTableFilters
} from "@/utils/staticTablesColumns";
import SelectProject from "@/components/SelectProject";
import FilterInput from "@/components/FilterInput";
import UploadPayinFeeModal from "@/components/modals/fees/UploadPayinFeeModal";
import CreatePayinFeeModal from "@/components/modals/fees/CreatePayinFeeModal";
import DeletePayinFeeModal from "@/components/modals/fees/DeletePayinFeeModal";

export default {
  name: "PayinFees",
  components: {
    MainHeader,
    SelectProject,
    FilterInput,
    UploadPayinFeeModal,
    CreatePayinFeeModal,
    DeletePayinFeeModal
  },
  data() {
    return {
      restoreFromQuery: false,
      sortBy: [],
      sortDesc: [],
      loadingTable: false,
      perPage: 10,
      page: 1,
      filter: [],
      perPageItems: [10, 25, 50, 100, 500],
      uploadDialog: false,
      createDialog: false,
      deleteModal: false,
      editedItem: null,
      deletedItem: {},
      buttons: [
        {
          title: "New Payin Fee",
          icon: "mdi-plus",
          click: () => (this.createDialog = true),
          loading: false
        },
        {
          title: "Export CSV",
          icon: "mdi-cloud-download",
          click: this.onExportCsv,
          loading: false
        },
        {
          title: "Upload",
          icon: "mdi-upload",
          click: this.showUploadDialog,
          loading: false
        }
      ]
    };
  },
  computed: {
    ...mapGetters({
      fees: "PayinFeesStore/fees",
      payinFeesPagination: "PayinFeesStore/payinFeesPagination",
      tenantsList: "RecordStore/getTenants"
    }),
    isAdmin() {
      return this.$store.getters["AuthorizationStore/getUserIsAdmin"];
    },
    isEditor() {
      return this.$store.getters["AuthorizationStore/getUserIsEditor"];
    },
    totalPages() {
      return this.payinFeesPagination.totalPages || 0;
    },
    filterStringPayload() {
      return this.filter.map(item => {
        return `${item.key}:${item.value}`;
      }).join(",");
    },
    filterStringURL: {
      get() {
        return this.filter.map(item => {
          return `${item.key}:${item.value}:${item.name}`;
        }).join(",");
      },
      set(newValue) {
        const filterArray = newValue.split(",");
        filterArray.forEach((item, index) => {
          const filterKeyValue = item.split(":");
          this.filter[index] = {};
          this.filter[index].key = filterKeyValue[0];
          this.filter[index].value = filterKeyValue[1];
          this.filter[index].name = filterKeyValue[2] || "";
        });

        return newValue;
      }
    }
  },
  watch: {
    perPage(newValue) {
      if (this.restoreFromQuery) return;
      this.addQueryToRouteWithReload("perPage", newValue);
      this.addQueryToRouteWithReload("page", 1);
    },
    filter: {
      handler() {
        if (this.restoreFromQuery) return;
        this.getFeesWithFilters();
      },
      deep: true
    },
    createDialog(newValue) {
      if (newValue === false) {
        this.editedItem = null;
      }
    }
  },
  beforeCreate() {
    const query = this.$route.query;
    if (query.tenant) {
      this.$store.dispatch("AppStore/setTenant", query.tenant);
    }
  },
  created() {
    this.payinFeesTableHeaders = payinFeesTableHeaders;
    this.payinFeesTableFilters = payinFeesTableFilters;
    if (!this.$store.getters["RecordStore/getTenants"]) {
      this.$store.dispatch("RecordStore/getTenants");
    }

    this.addQueryToRouteWithReload("tenant", this.$store.getters["AppStore/getTenant"]);

    if (this.$route.query) {
      this.restoreFromQuery = true;
      const query = this.$route.query;
      this.perPage = parseInt(query.perPage) || 10;
      this.page = parseInt(query.page) || 1;
      let sort = query.sort || [];

      if (sort.length) {
        if (typeof sort === "string") sort = [sort];

        const sortBy = [];
        const sortDesc = [];
        sort.forEach(item => {
          const array = item.split(",");
          sortBy.push(array[0]);
          sortDesc.push(array[1] !== "asc");
        });
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;
      }

      if (query?.filter?.length) {
        this.filterStringURL = query.filter;
      }
    }

    this.getFees();
    this.$store.dispatch("ActiveCountryStore/getList");
  },
  methods: {
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.createDialog = true;
    },

    deleteItem(item) {
      this.deletedItem = Object.assign({}, item);
      this.deleteModal = true;
    },

    async onExportCsv() {
      try {
        this.$set(this.buttons[1], "loading", true);

        await this.$store
          .dispatch("PayinFeesStore/exportCsv", {
            sort: this.sortBy.map(
              (val, index) => `${val},${this.sortDesc[index] ? "desc" : "asc"}`
            ),
            filter: this.filterStringPayload
          })
          .then(res => {
            // Use a Blob object URL instead of a data: URI. data: URIs are size-capped by
            // the browser (~2MB in Chrome) and silently truncate large downloads.
            const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `payin_fees_${this.$store.getters["AppStore/getTenant"]}_${new Date().toISOString()}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            this.$nextTick().then(() =>
              this.$set(this.buttons[1], "loading", false)
            );
          });
      } finally {
        this.$set(this.buttons[1], "loading", false);
      }
    },
    async getFees() {
      try {
        this.loadingTable = true;

        await this.$store
          .dispatch("PayinFeesStore/getFees", {
            page: this.page,
            size: this.perPage,
            sort: this.sortBy.map(
              (val, index) => `${val},${this.sortDesc[index] ? "desc" : "asc"}`
            ),
            filter: this.filterStringPayload
          });
      } finally {
        this.loadingTable = false;
        this.restoreFromQuery = false;
      }
    },
    getFeesWithSort() {
      if (this.loadingTable) return;
      this.page = 1;
      let sort = this.sortBy.map((val, index) => {
        return `${val},${this.sortDesc[index] ? "desc" : "asc"}`;
      });
      this.addQueryToRouteWithReload("page", 1);
      this.addQueryToRouteWithReload("sort", sort);
    },
    getFeesWithFilters() {
      this.addQueryToRouteWithReload("filter", this.filterStringURL);
      this.addQueryToRouteWithReload("perPage", this.perPage);
      this.addQueryToRouteWithReload("page", 1);
    },
    onChangedSelectedProject() {
      this.addQueryToRouteWithReload("tenant", this.$store.getters["AppStore/getTenant"]);
      this.addQueryToRouteWithReload("page", 1);
    },
    addQueryToRouteWithReload(name, value) {
      this.$router.replace({
        query: Object.assign({
          ...this.$route.query
        }, {
          [name]: value
        })},
      () => {}
      );
    },
    onChangePage() {
      this.addQueryToRouteWithReload("page", this.page);
    },
    showUploadDialog() {
      this.uploadDialog = true;
    }
  }
};
</script>

<style scoped lang="scss">
.select-per-page {
  margin: 0;
  padding: 0;
  max-width: 140px;
  float: right;
}

.b-pagination {
  float: left;
}

.table {
  &::v-deep {
    font-size: 14px;

    th {
      box-shadow: none !important;
    }
    td:not(.v-data-table__mobile-row),
    th:not(.v-data-table__mobile-row) {
      border-bottom: 1px solid #f2f2f2 !important;
    }
  }
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 10px 16px;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  padding: 10px 16px;
}
</style>
